import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, User, MapPin, Book, Users, Church, Sparkles, Activity, History, Zap } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { DatePopover } from '@/components/ui/date-popover';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { normalizeBookName } from '@/lib/utils/bible-utils';

interface PersonProfileModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    person: Person;
    personInPassage?: PersonInPassage;
    bookName?: string;
}

export function PersonProfileModal({
    isOpen,
    onOpenChange,
    person,
    personInPassage,
    bookName
}: PersonProfileModalProps) {
    const isDarkMode = useDarkMode();

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl h-[90vh] p-0 overflow-hidden border-none bg-background shadow-2xl rounded-3xl flex flex-col">
                <VisuallyHidden>
                    <DialogTitle>{personInPassage?.displayNameInPassage || person.personName} Profile</DialogTitle>
                </VisuallyHidden>

                {/* Header Action Row - Removed manual X as DialogContent provides one */}
                <div className="px-8 pt-8 pb-4 shrink-0 bg-background border-b border-border/5">
                    <div className="space-y-4">
                        {personInPassage?.roleInPassage && (
                            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-primary/60">
                                {personInPassage.roleInPassage}
                            </span>
                        )}
                        <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground">
                            {personInPassage?.displayNameInPassage || person.personName}
                        </h2>
                    </div>
                </div>

                <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
                    <div className="px-8 py-2 border-b border-border/10 bg-muted/5">
                        <TabsList className="bg-transparent gap-6 p-0 h-10">
                            {[
                                { id: 'overview', label: 'Overview', icon: User },
                                { id: 'biography', label: 'Life & Story', icon: History },
                                { id: 'impact', label: 'Impact & Faith', icon: Sparkles },
                            ].map((tab) => (
                                <TabsTrigger
                                    key={tab.id}
                                    value={tab.id}
                                    className="bg-transparent border-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground relative h-10 px-0 rounded-none transition-all group"
                                >
                                    <div className="flex items-center gap-2">
                                        <tab.icon className="h-4 w-4" />
                                        <span className="text-[11px] font-bold uppercase tracking-widest">{tab.label}</span>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary scale-x-0 group-data-[state=active]:scale-x-100 transition-transform origin-left" />
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>

                    <ScrollArea className="flex-1 px-8">
                        <div className="max-w-3xl mx-auto py-8 space-y-12">

                            <TabsContent value="overview" className="mt-0 space-y-10 outline-none">
                                {/* 1. Quick Summary */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 font-mono">
                                        <Zap className="h-3 w-3" />
                                        Core Identity
                                    </div>
                                    <p className="text-xl md:text-2xl leading-relaxed text-foreground/90 italic font-serif border-l-3 border-primary/20 pl-8 py-1">
                                        {person.summary}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Stats */}
                                    {person.lifespan && (
                                        <div className="p-6 rounded-2xl bg-muted/20 border border-border/40 space-y-6">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 font-mono">Vital Stats</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                {person.lifespan.birthDate && (
                                                    <div>
                                                        <p className="text-[9px] font-bold text-muted-foreground/40 uppercase mb-1">Birth</p>
                                                        <DatePopover date={person.lifespan.birthDate} alternativeDates={person.lifespan.alternativeDates} title="Birth" />
                                                    </div>
                                                )}
                                                {person.lifespan.deathDate && (
                                                    <div>
                                                        <p className="text-[9px] font-bold text-muted-foreground/40 uppercase mb-1">Death</p>
                                                        <DatePopover date={person.lifespan.deathDate} alternativeDates={person.lifespan.alternativeDates} title="Death" />
                                                    </div>
                                                )}
                                            </div>
                                            {person.lifespan.lifespan && (
                                                <div className="pt-2 border-t border-border/10">
                                                    <p className="text-[9px] font-bold text-muted-foreground/40 uppercase mb-0.5">Known Span</p>
                                                    <p className="text-sm font-medium text-foreground/80">{person.lifespan.lifespan}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Geography */}
                                    {person.geography && (
                                        <div className="p-6 rounded-2xl bg-muted/20 border border-border/40 space-y-6">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 font-mono">Geography</p>
                                            <div className="space-y-4">
                                                {person.geography.birthPlace && (
                                                    <div>
                                                        <p className="text-[9px] font-bold text-muted-foreground/40 uppercase mb-1.5">Origin</p>
                                                        <div className="flex items-center gap-2 text-sm font-medium text-foreground/90">
                                                            <MapPin className="h-3 w-3 text-primary/60" />
                                                            {person.geography.birthPlace}
                                                        </div>
                                                    </div>
                                                )}
                                                {person.geography.primaryLocations && person.geography.primaryLocations.length > 0 && (
                                                    <div>
                                                        <p className="text-[9px] font-bold text-muted-foreground/40 uppercase mb-2">Major Areas</p>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {person.geography.primaryLocations.map((loc, i) => (
                                                                <Badge key={i} variant="secondary" className="text-[10px] px-2 h-5 bg-primary/5 text-primary/80 border-none">{loc}</Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>


                                {/* Legacy & Path Content Moved Here */}
                                <div className="pt-8 border-t border-border/10 space-y-12">
                                    {/* Journey */}
                                    {person.itinerary && person.itinerary.length > 0 && (
                                        <div className="space-y-8">
                                            <div className="flex items-center gap-3">
                                                <div className="p-1.5 rounded-lg bg-primary/10">
                                                    <Activity className="w-4 h-4 text-primary" />
                                                </div>
                                                <h4 className="font-bold text-[11px] uppercase tracking-widest text-foreground/70">Itinerant Journey</h4>
                                            </div>

                                            <div className="space-y-3 relative pl-9 border-l border-primary/10">
                                                {person.itinerary.map((visit, i) => (
                                                    <div key={i} className="flex gap-6 p-5 rounded-2xl bg-muted/10 border border-border/5 group hover:bg-muted/20 hover:border-border/20 transition-all duration-300">
                                                        <div className="flex flex-col items-center gap-1 shrink-0 pt-1">
                                                            <div className="w-10 h-10 rounded-xl bg-background flex items-center justify-center border border-border/20 text-primary shadow-sm group-hover:scale-110 transition-transform">
                                                                <MapPin className="h-5 w-5" />
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2 py-1">
                                                            <div className="flex items-center gap-4">
                                                                <h5 className="font-serif font-bold text-xl text-foreground/90">{visit.placeName}</h5>
                                                                {visit.date && (
                                                                    <div className="scale-90 origin-left">
                                                                        <DatePopover date={visit.date} alternativeDates={visit.alternativeDates} title={`Visit to ${visit.placeName}`} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {visit.activity && (
                                                                <p className="text-sm text-muted-foreground leading-relaxed italic border-l-2 border-primary/5 pl-4 py-1">
                                                                    {visit.activity}
                                                                </p>
                                                            )}
                                                            {visit.duration && (
                                                                <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/40 flex items-center gap-2">
                                                                    <div className="w-1 h-1 rounded-full bg-primary/20" />
                                                                    Duration: {visit.duration}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                        {/* Relationships */}
                                        <div className="space-y-8">
                                            {person.relationships && person.relationships.length > 0 && (
                                                <div className="space-y-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-1.5 rounded-lg bg-blue-500/10">
                                                            <Users className="w-4 h-4 text-blue-500" />
                                                        </div>
                                                        <h4 className="font-bold text-[11px] uppercase tracking-widest text-blue-900/60">Relationships</h4>
                                                    </div>
                                                    <div className="grid grid-cols-1 gap-6 pl-9 border-l border-blue-500/10">
                                                        {person.relationships.map((rel, i) => (
                                                            <div key={i} className="flex flex-col group">
                                                                <span className="text-sm font-bold text-foreground/90 group-hover:text-blue-600 transition-colors cursor-default tracking-tight">{rel.personName}</span>
                                                                <span className="text-[10px] uppercase text-blue-500/50 font-bold tracking-widest mt-0.5">{rel.relationshipType}</span>
                                                                {rel.description && <p className="text-xs text-muted-foreground mt-2 leading-relaxed italic border-l border-blue-500/5 pl-3">"{rel.description}"</p>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* References */}
                                        <div className="space-y-8">
                                            {person.biblicalReferences && person.biblicalReferences.length > 0 && (
                                                <div className="space-y-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-1.5 rounded-lg bg-green-600/10">
                                                            <Book className="w-4 h-4 text-green-600" />
                                                        </div>
                                                        <h4 className="font-bold text-[11px] uppercase tracking-widest text-green-900/60">Canonical Footprint</h4>
                                                    </div>
                                                    <div className="space-y-8 pl-9 border-l border-green-600/10">
                                                        {person.biblicalReferences.map((ref, i) => (
                                                            <div key={i} className="space-y-3 group">
                                                                <div className="flex items-center gap-3 flex-wrap">
                                                                    <span className="text-sm font-bold text-foreground/90 group-hover:text-green-700 transition-colors">{normalizeBookName(ref.book)}</span>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {ref.passages.map((p, j) => (
                                                                            <Badge key={j} variant="outline" className="text-[9px] px-2 h-4 border-green-600/10 text-green-700/60 bg-green-500/5 font-mono">{p}</Badge>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                {ref.context && <p className="text-xs text-muted-foreground leading-relaxed italic border-l border-green-500/5 pl-3">"{ref.context}"</p>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>


                                {person.alternateNames && person.alternateNames.length > 0 && (
                                    <div className="p-6 rounded-2xl border border-border/10 space-y-4">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 font-mono">Also Known As</p>
                                        <div className="flex flex-wrap gap-3">
                                            {person.alternateNames.map((name, i) => (
                                                <Badge key={i} variant="outline" className="bg-muted/10 border-border/40 text-muted-foreground py-1 px-3 rounded-lg">{name}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="biography" className="mt-0 space-y-12 outline-none">
                                {person.description && (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 font-mono">
                                            <History className="h-3 w-3" />
                                            Life Narrative
                                        </div>
                                        <p className="text-lg leading-relaxed text-foreground/80 whitespace-pre-line font-serif">
                                            {person.description}
                                        </p>
                                    </div>
                                )}

                                {(person.biography || person.historicalContext) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-8 border-t border-border/10">
                                        <div className="space-y-8">
                                            {person.biography?.occupation && (
                                                <section className="space-y-2">
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary/60 font-mono">Occupations</h4>
                                                    <div className="flex flex-wrap gap-2 pt-1">
                                                        {person.biography.occupation.map((occ, i) => (
                                                            <Badge key={i} variant="outline" className="bg-primary/5 text-[10px] text-primary/70 border-primary/20">{occ}</Badge>
                                                        ))}
                                                    </div>
                                                </section>
                                            )}
                                            {person.biography?.socialStatus && (
                                                <section className="space-y-2">
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary/60 font-mono">Social World</h4>
                                                    <p className="text-sm text-foreground/80 leading-relaxed italic">{person.biography.socialStatus}</p>
                                                </section>
                                            )}
                                            {person.historicalContext?.period && (
                                                <section className="space-y-2">
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary/60 font-mono">Historical Era</h4>
                                                    <p className="text-sm font-medium text-foreground/80">{person.historicalContext.period}</p>
                                                </section>
                                            )}
                                        </div>
                                        <div className="space-y-8">
                                            {person.biography?.tribalAffiliation && (
                                                <section className="space-y-2">
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary/60 font-mono">Heritage</h4>
                                                    <p className="text-sm text-foreground/80 leading-relaxed font-serif text-lg">{person.biography.tribalAffiliation}</p>
                                                </section>
                                            )}
                                            {person.biography?.education && (
                                                <section className="space-y-2">
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary/60 font-mono">Education</h4>
                                                    <p className="text-sm text-foreground/80 leading-relaxed italic">{person.biography.education}</p>
                                                </section>
                                            )}
                                            {person.historicalContext?.politicalContext && (
                                                <section className="space-y-2">
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary/60 font-mono">Political Climate</h4>
                                                    <p className="text-sm text-foreground/70 leading-relaxed">{person.historicalContext.politicalContext}</p>
                                                </section>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="impact" className="mt-0 space-y-12 outline-none">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                    {/* Left Column: Ministry & Character */}
                                    <div className="space-y-12">
                                        {person.ministry && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-1.5 rounded-lg bg-primary/10">
                                                        <Sparkles className="w-4 h-4 text-primary" />
                                                    </div>
                                                    <h4 className="font-bold text-[11px] uppercase tracking-widest text-foreground/70">Ministry & Calling</h4>
                                                </div>
                                                <div className="space-y-6 pl-9 border-l border-primary/10">
                                                    {person.ministry.role && (
                                                        <div className="flex flex-wrap gap-2">
                                                            {person.ministry.role.map((r, i) => (
                                                                <Badge key={i} variant="secondary" className="bg-primary/5 text-primary/80 border border-primary/10 text-[10px] font-bold tracking-wide">{r}</Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {person.ministry.callingEvent && (
                                                        <p className="text-sm text-muted-foreground leading-relaxed italic border-l-2 border-primary/5 pl-4">"{person.ministry.callingEvent}"</p>
                                                    )}
                                                    {person.ministry.keyEvents && person.ministry.keyEvents.length > 0 && (
                                                        <ul className="space-y-4">
                                                            {person.ministry.keyEvents.map((e, i) => (
                                                                <li key={i} className="text-sm text-foreground/80 flex items-start gap-3 group">
                                                                    <div className="h-1.5 w-1.5 rounded-full bg-primary/20 mt-2 shrink-0 group-hover:bg-primary transition-colors" />
                                                                    {e}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        )}


                                        {person.character && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-1.5 rounded-lg bg-amber-500/10">
                                                        <User className="w-4 h-4 text-amber-500" />
                                                    </div>
                                                    <h4 className="font-bold text-[11px] uppercase tracking-widest text-amber-900/60 ">Character & Virtue</h4>
                                                </div>
                                                <div className="space-y-6 pl-9 border-l border-amber-500/10">
                                                    <div className="grid grid-cols-1 gap-8">
                                                        {person.character.strengths && (
                                                            <div className="space-y-2">
                                                                <p className="text-[9px] font-bold uppercase text-amber-900/40 tracking-widest font-mono">Strengths</p>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {person.character.strengths.map((s, i) => (
                                                                        <Badge key={i} variant="outline" className="text-[10px] border-amber-500/10 text-amber-800/80 bg-amber-500/5 h-auto whitespace-normal py-1 text-left">
                                                                            {s}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {person.character.weaknesses && (
                                                            <div className="space-y-2">
                                                                <p className="text-[9px] font-bold uppercase text-amber-900/40 tracking-widest font-mono">Weaknesses</p>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {person.character.weaknesses.map((w, i) => (
                                                                        <Badge key={i} variant="outline" className="text-[10px] border-amber-500/10 text-amber-800/80 bg-amber-500/5 h-auto whitespace-normal py-1 text-left">
                                                                            {w}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {person.character.development && (
                                                        <p className="text-sm text-foreground/80 leading-relaxed italic border-l-2 border-amber-500/5 pl-4">"{person.character.development}"</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right Column: Theology & Tradition */}
                                    <div className="space-y-12">
                                        {person.theology && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-1.5 rounded-lg bg-emerald-500/10">
                                                        <Book className="w-4 h-4 text-emerald-500" />
                                                    </div>
                                                    <h4 className="font-bold text-[11px] uppercase tracking-widest text-emerald-900/60">Theological Echoes</h4>
                                                </div>
                                                <div className="space-y-6 pl-9 border-l border-emerald-500/10">
                                                    {person.theology.theologicalThemes && (
                                                        <div className="flex flex-wrap gap-2">
                                                            {person.theology.theologicalThemes.map((t, i) => <Badge key={i} variant="outline" className="text-emerald-700/80 border-emerald-500/10 bg-emerald-500/5 text-[10px]">{t}</Badge>)}
                                                        </div>
                                                    )}
                                                    {person.theology.keyTeachings && (
                                                        <ul className="space-y-4">
                                                            {person.theology.keyTeachings.map((teaching, i) => (
                                                                <li key={i} className="text-sm text-foreground/80 leading-relaxed italic border-l-2 border-emerald-500/5 pl-4">"{teaching}"</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {person.tradition && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-1.5 rounded-lg bg-blue-500/10">
                                                        <Church className="w-4 h-4 text-blue-500" />
                                                    </div>
                                                    <h4 className="font-bold text-[11px] uppercase tracking-widest text-blue-900/60">Historical Memory</h4>
                                                </div>
                                                <div className="space-y-6 pl-9 border-l border-blue-500/10">
                                                    {person.tradition.laterLife && (
                                                        <p className="text-sm text-foreground/80 leading-relaxed">{person.tradition.laterLife}</p>
                                                    )}
                                                    {person.tradition.martyrdom && (
                                                        <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                                                            <p className="text-[10px] font-bold uppercase text-red-900/40 mb-2 font-mono tracking-widest">Martyrdom Legacy</p>
                                                            <p className="text-sm italic text-red-900/70 font-serif leading-relaxed">{person.tradition.martyrdom}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </TabsContent>


                        </div>
                    </ScrollArea>
                </Tabs>

                {/* Footer / Attribution */}
                <div className="p-6 border-t border-border/20 bg-muted/30 shrink-0 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="h-1 w-1 rounded-full bg-primary/40" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Comprehensive BibleGlen Profile</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="rounded-full px-6">
                        Done
                    </Button>
                </div>
            </DialogContent >
        </Dialog >
    );
}
